import { useEffect, useRef, useCallback } from 'react';
import { WebSocketMessage } from '../types';
import { useMessageStore } from '../store/messageStore';
import { apiService } from '../services/api';

interface UseWebSocketOptions {
  reconnectAttempts?: number;
  reconnectInterval?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const {
    reconnectAttempts = 5, // Standard reconnection attempts
    reconnectInterval = 3000, // Standard reconnection interval
    onConnect,
    onDisconnect,
    onError,
  } = options;

  // WebSocket state refs
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectCountRef = useRef(0);
  const isConnectingRef = useRef(false);

  // Zustand store slices (avoids infinite re-render issue)
  const addMessage = useMessageStore((state) => state.addMessage);
  const updateMessage = useMessageStore((state) => state.updateMessage);
  const setWebSocketStatus = useMessageStore((state) => state.setWebSocketStatus);
  const isConnected = useMessageStore((state) => state.wsConnected);

  const connect = useCallback(() => {
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Clean up any existing connection first
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    isConnectingRef.current = true;

    try {
      const wsUrl = apiService.getWebSocketUrl();
      console.log('Connecting to WebSocket:', wsUrl);

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ WebSocket connected successfully');
        isConnectingRef.current = false;
        reconnectCountRef.current = 0;

        // Update connection status
        setWebSocketStatus(true);
        onConnect?.();
      };

      wsRef.current.onmessage = (event: MessageEvent) => {
        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          console.log('WebSocket message received:', data);

          switch (data.type) {
            case 'message_sent':
            case 'message_received':
              addMessage(data.message);
              break;

            case 'message_status_updated':
              updateMessage(data.message.id, data.message);
              break;

            default:
              console.warn('Unknown WebSocket message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event: CloseEvent) => {
        console.log(`WebSocket disconnected: code=${event.code}, reason="${event.reason}"`);
        isConnectingRef.current = false;

        if (useMessageStore.getState().wsConnected) {
          setWebSocketStatus(false);
        }

        onDisconnect?.();

        // Attempt to reconnect on unexpected disconnection
        if (event.code !== 1000 && event.code !== 1001 && reconnectCountRef.current < reconnectAttempts) {
          console.log(`🔄 Connection lost (code ${event.code}), attempting to reconnect... Attempt ${reconnectCountRef.current + 1}/${reconnectAttempts}`);
          scheduleReconnect();
        } else {
          console.log('WebSocket connection closed - max attempts reached or normal closure');
          reconnectCountRef.current = 0; // Reset for future manual connections
        }
      };

      wsRef.current.onerror = (error: Event) => {
        console.error('WebSocket error:', error);
        isConnectingRef.current = false;

        if (useMessageStore.getState().wsConnected) {
          setWebSocketStatus(false);
        }

        onError?.(error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      isConnectingRef.current = false;

      if (useMessageStore.getState().wsConnected) {
        setWebSocketStatus(false);
      }
    }
  }, [
    addMessage,
    updateMessage,
    onConnect,
    onDisconnect,
    onError,
    reconnectAttempts,
  ]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectCountRef.current >= reconnectAttempts) {
      console.log('Max reconnection attempts reached');
      setWebSocketStatus(false);
      return;
    }

    reconnectCountRef.current += 1;
    console.log(
      `Scheduling reconnection attempt ${reconnectCountRef.current}/${reconnectAttempts} in ${reconnectInterval}ms`
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      if (!isConnectingRef.current && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
        connect();
      }
    }, reconnectInterval);
  }, [connect, reconnectAttempts, reconnectInterval]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }

    isConnectingRef.current = false;
    reconnectCountRef.current = 0;

    if (useMessageStore.getState().wsConnected) {
      setWebSocketStatus(false);
    }
  }, []);

  const sendMessage = useCallback((message: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const ping = useCallback(() => {
    return sendMessage({ type: 'ping', timestamp: Date.now() });
  }, [sendMessage]);

  // Connect on mount and cleanup on unmount
  useEffect(() => {
    // Add a small delay to prevent immediate reconnection loops
    const connectTimer = setTimeout(() => {
      connect();
    }, 2000);
    
    return () => {
      clearTimeout(connectTimer);
      disconnect();
    };
  }, [connect, disconnect]);

  // Ping every 30 seconds to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        ping(); // Just ping, don't force reconnection on ping failure
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, [ping, reconnectAttempts, scheduleReconnect]);

  return {
    connect,
    disconnect,
    sendMessage,
    ping,
    isConnected,
  };
};
